import { zonedTimeToUtc, utcToZonedTime, format } from 'date-fns-tz';
import {
  startOfDay,
  endOfDay,
  isAfter,
  isBefore,
  addDays,
  isValid
} from 'date-fns';

const CST_TIMEZONE = 'America/Chicago';
const CST_FORMAT = 'MMM d, yyyy h:mm a zzz';

export function getCurrentCSTTime(): Date {
  return utcToZonedTime(new Date(), CST_TIMEZONE);
}

export function formatCSTTime(date: Date | number): string {
  try {
    // Convert to Date object if timestamp
    const dateObj = typeof date === 'number' ? new Date(date) : date;

    // Validate date
    if (!isValid(dateObj)) {
      throw new Error('Invalid date');
    }

    // Convert to CST timezone and format
    const cstDate = utcToZonedTime(dateObj, CST_TIMEZONE);
    return format(cstDate, CST_FORMAT, { timeZone: CST_TIMEZONE });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
}

export function isFromPreviousCSTDay(timestamp: number): boolean {
  try {
    if (!isValid(new Date(timestamp))) {
      return false;
    }

    const cstNow = utcToZonedTime(new Date(), CST_TIMEZONE);
    const cstTimestamp = utcToZonedTime(new Date(timestamp), CST_TIMEZONE);

    // Get start of current day in CST
    const cstStartOfToday = startOfDay(cstNow);

    // Handle DST transition
    const timestampInCST = format(cstTimestamp, 'yyyy-MM-dd', {
      timeZone: CST_TIMEZONE,
    });
    const todayInCST = format(cstStartOfToday, 'yyyy-MM-dd', {
      timeZone: CST_TIMEZONE,
    });

    return timestampInCST < todayInCST;
  } catch (error) {
    console.error('Error checking previous day:', error);
    return false;
  }
}

export function isInSameOrPreviousCSTDay(timestamp: number): boolean {
  try {
    if (!isValid(new Date(timestamp))) {
      return false;
    }

    const cstNow = utcToZonedTime(new Date(), CST_TIMEZONE);
    const cstTimestamp = utcToZonedTime(new Date(timestamp), CST_TIMEZONE);
    const cstStartOfToday = startOfDay(cstNow);
    const cstStartOfYesterday = startOfDay(addDays(cstStartOfToday, -1));

    return (
      isAfter(cstTimestamp, cstStartOfYesterday) &&
      isBefore(cstTimestamp, addDays(cstStartOfToday, 1))
    );
  } catch (error) {
    console.error('Error checking day:', error);
    return false;
  }
}

export function calculateDuration(start: number, end: number): string {
  try {
    if (!isValid(new Date(start)) || !isValid(new Date(end))) {
      throw new Error('Invalid start or end time');
    }

    const durationMs = end - start;
    if (durationMs < 0) {
      throw new Error('End time is before start time');
    }

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return '0h 0m';
  }
}

export interface DayBounds {
  start: number;
  end: number;
}

export function getCSTDayBounds(date: Date = new Date()): DayBounds {
  try {
    if (!isValid(date)) {
      throw new Error('Invalid date');
    }

    const cstDate = utcToZonedTime(date, CST_TIMEZONE);
    const startOfCSTDay = startOfDay(cstDate);
    const endOfCSTDay = endOfDay(cstDate);

    return {
      start: zonedTimeToUtc(startOfCSTDay, CST_TIMEZONE).getTime(),
      end: zonedTimeToUtc(endOfCSTDay, CST_TIMEZONE).getTime(),
    };
  } catch (error) {
    console.error('Error getting day bounds:', error);
    const now = Date.now();
    return {
      start: now,
      end: now + 24 * 60 * 60 * 1000,
    };
  }
}

export function isSameCSTPeriod(date1: Date, date2: Date): boolean {
  try {
    if (!isValid(date1) || !isValid(date2)) {
      return false;
    }

    const cstDate1 = utcToZonedTime(date1, CST_TIMEZONE);
    const cstDate2 = utcToZonedTime(date2, CST_TIMEZONE);

    return startOfDay(cstDate1).getTime() === startOfDay(cstDate2).getTime();
  } catch (error) {
    console.error('Error comparing periods:', error);
    return false;
  }
}